import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import WebsiteEditor from './WebsiteEditor';

// Icon Components
const HomeIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
  </svg>
);

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

interface WebsiteData {
  id: string;
  domain: string;
  status: string;
  contentSetupComplete: boolean;
  businessName?: string;
  trade?: string;
  services?: string[];
  githubRepo?: string | null;
}

const WebsiteEditPage: React.FC = () => {
  const { id: websiteId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch website data to determine which form to show
  const { data: websiteData, isLoading, error } = useQuery<WebsiteData>({
    queryKey: ['website-edit', websiteId],
    queryFn: async () => {
      const response = await axios.get(`/api/websites/${websiteId}`);
      return response.data.data.website;
    },
    enabled: !!websiteId,
    retry: 2,
  });

  const handleClose = () => navigate('/dashboard');

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2">
            <li>
              <Link to="/dashboard" className="text-muted hover:text-primary transition-colors">
                <HomeIcon className="w-5 h-5" />
              </Link>
            </li>
            <li><ChevronRightIcon className="w-4 h-4 text-zinc-300" /></li>
            <li><span className="text-sm text-primary font-medium">Edit Website</span></li>
          </ol>
        </nav>

        <div className="bg-red-50 border border-red-100 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-red-900">Error loading website</h3>
              <p className="text-sm text-red-700 mt-1">Unable to load website data. Please try again.</p>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 mt-3 text-sm font-medium text-red-700 hover:text-red-800"
              >
                Return to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // A generated/deployed site already has its content as JSON in its GitHub repo, so
  // edit that directly — regardless of the contentSetupComplete flag, which is not set
  // for pipeline-generated sites. This avoids asking the user to re-enter business info
  // they never filled in: we load and edit the existing JSON instead.
  if (websiteData?.githubRepo) {
    const previewUrl = import.meta.env.DEV && websiteData.githubRepo === 'showcase:local'
      ? `${window.location.origin}/showcase-site`
      : undefined;
    return <WebsiteEditor websiteId={websiteData.id} domain={websiteData.domain} previewUrl={previewUrl} onClose={handleClose} />;
  }

  // No GitHub repo means the site has not been generated yet, so there is no content
  // to edit. Say so plainly — there used to be two intake forms here, but they posted
  // to endpoints the backend never had, so they only looked like they saved.
  return (
    <div className="space-y-6">
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="flex items-center gap-2">
          <li>
            <Link to="/dashboard" className="text-muted hover:text-primary transition-colors">
              <HomeIcon className="w-5 h-5" />
            </Link>
          </li>
          <li><ChevronRightIcon className="w-4 h-4 text-zinc-300" /></li>
          <li><span className="text-sm text-primary font-medium">Edit Website</span></li>
        </ol>
      </nav>

      <div className="bg-white border border-zinc-200 rounded-xl p-6">
        <h3 className="font-display font-semibold text-primary">
          {websiteData?.domain || 'This site'} isn’t ready to edit yet
        </h3>
        <p className="text-sm text-muted mt-2 max-w-prose">
          We’re still building it. Once it’s live you’ll be able to edit every page right
          here. Need something changed in the meantime, or want to send us your photos and
          business details? Email us and we’ll take care of it.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <a
            href={`mailto:hello@foundlio.com?subject=${encodeURIComponent(
              `Website content — ${websiteData?.domain || websiteId}`
            )}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline underline-offset-4"
          >
            hello@foundlio.com
          </a>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-primary transition-colors"
          >
            Return to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WebsiteEditPage;
